import React, { useState, useRef } from 'react';
import { useSwipeable } from 'react-swipeable';
import { ProductImage, productImageSrc } from '../types';

interface ImageCarouselProps {
  images?: ProductImage[];
  size?: number;
  fill?: boolean;
  /** `cover` crops to fill; `contain` shows the full image uncropped. */
  objectFit?: 'cover' | 'contain';
  /** Use full-resolution originals when available. */
  quality?: 'preview' | 'original';
  /** When set, the image area is clickable (carousel arrows still change slide only). */
  onImageClick?: (index: number) => void;
  /** Required for accessibility when `onImageClick` is set. */
  imageClickLabel?: string;
}

export const ImageCarousel: React.FC<ImageCarouselProps> = ({
  images,
  size = 100,
  fill,
  objectFit = 'cover',
  quality = 'preview',
  onImageClick,
  imageClickLabel,
}) => {
  const [idx, setIdx] = useState(0);
  const idxRef = useRef(idx);
  idxRef.current = idx;
  const multi = Boolean(images && images.length > 1);

  const swipeHandlers = useSwipeable({
    delta: 40,
    preventScrollOnSwipe: multi,
    trackMouse: multi,
    trackTouch: multi,
    onSwipedLeft: multi
      ? () => setIdx(i => (i + 1 + (images?.length ?? 1)) % (images?.length ?? 1))
      : undefined,
    onSwipedRight: multi
      ? () => setIdx(i => (i - 1 + (images?.length ?? 1)) % (images?.length ?? 1))
      : undefined,
    onTap: multi && onImageClick ? () => onImageClick(idxRef.current) : undefined,
  });

  const dims = fill
    ? { width: '100%', height: '100%' }
    : { width: size, height: size, flexShrink: 0 as const };

  const a11yProps = onImageClick
    ? {
        role: 'button' as const,
        tabIndex: 0 as const,
        'aria-label': imageClickLabel,
        ...(!multi ? { onClick: () => onImageClick(idx) } : {}),
        onKeyDown: (e: React.KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onImageClick(idx);
          }
        },
      }
    : {};

  const pointerStyle: React.CSSProperties | undefined = onImageClick
    ? { cursor: objectFit === 'contain' ? 'zoom-in' : 'pointer' }
    : undefined;

  if (!images || images.length === 0) {
    return (
      <div
        {...a11yProps}
        style={{
          ...dims,
          backgroundColor: '#EDE8E0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: fill ? 0 : 12,
          color: '#aaa',
          fontSize: 12,
          marginRight: fill ? 0 : 12,
          ...pointerStyle,
        }}
      >
        No image
      </div>
    );
  }

  const nav = (dir: number) => (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIdx(i => (i + dir + images.length) % images.length);
  };

  const btnStyle: React.CSSProperties = {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    zIndex: 1,
    border: 'none',
    background: 'rgba(0,0,0,0.35)',
    color: '#fff',
    cursor: 'pointer',
    padding: '6px 10px',
    fontSize: 28,
    lineHeight: 1,
    borderRadius: 8,
    minWidth: 44,
    minHeight: 44,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const imgEl = (
    <img
      src={productImageSrc(images[idx], quality)}
      alt="Product"
      style={{ width: '100%', height: '100%', objectFit }}
    />
  );

  return (
    <div
      {...a11yProps}
      style={{
        ...dims,
        position: 'relative',
        overflow: 'hidden',
        borderRadius: fill ? 0 : 12,
        backgroundColor: '#000',
        marginRight: fill ? 0 : 12,
        ...pointerStyle,
      }}
    >
      {multi ? (
        <div
          ref={swipeHandlers.ref}
          onMouseDown={swipeHandlers.onMouseDown}
          style={{ position: 'absolute', inset: 0, zIndex: 0 }}
        >
          {imgEl}
        </div>
      ) : (
        imgEl
      )}
      {multi && (
        <>
          <button type="button" aria-label="Previous image" onClick={nav(-1)} style={{ ...btnStyle, left: 6 }}>
            ‹
          </button>
          <button type="button" aria-label="Next image" onClick={nav(1)} style={{ ...btnStyle, right: 6 }}>
            ›
          </button>
          <div
            style={{
              position: 'absolute',
              bottom: 4,
              width: '100%',
              textAlign: 'center',
              fontSize: 10,
              color: '#fff',
              textShadow: '0 1px 3px rgba(0,0,0,0.6)',
              pointerEvents: 'none',
              zIndex: 1,
            }}
          >
            {idx + 1}/{images.length}
          </div>
        </>
      )}
    </div>
  );
};
